import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button, Card, OverlayTrigger, Tooltip, Dropdown } from "react-bootstrap";
import { Task } from "../../state/Task";
import { LifePart } from "../../state/LifePart";
import Icon from "../Icon";
import {
    faChevronLeft,
    faChevronRight,
    faTrash,
    faGripVertical,
    faPlus,
    faCalendarDay,
} from "@fortawesome/free-solid-svg-icons";
import { format, isSameMonth, parse } from "date-fns";
import { ru } from "date-fns/locale";

import { useReadonly } from "@/contexts/readonlyContext";

const MONTH_NAMES_SHORT = [
    "Янв", "Фев", "Мар", "Апр", "Май", "Июн",
    "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек",
];
const MONTH_NAMES = [
    "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
    "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

type Props = {
    lifepartUuid: string;
    tasks: Record<string, Task>;
    roles: { objects: Record<string, LifePart> };
    sortedRoles: LifePart[];
    onTaskMonthUpdate: (taskUuid: string, month: string[]) => void;
    onOpenTask?: (taskUuid: string) => void;
    selectedMonth?: string | null;
    onSelectedMonthChange?: (monthKey: string) => void;
    currentLifePartUuid?: string;
    currentStepPoint?: number | null;
};

/* ───一小卡 в месяце ─── */
function MonthTaskCard({
    task,
    lifePartColor,
    onRemove,
    onDragStart,
    draggable,
    onOpenTask,
    readonly,
}: {
    task: Task;
    lifePartColor: string;
    onRemove: (e: React.MouseEvent) => void;
    onDragStart: (e: React.DragEvent) => void;
    draggable: boolean;
    onOpenTask?: (taskUuid: string) => void;
    readonly: boolean;
}) {
    const cardRef = useRef<HTMLDivElement>(null);

    const handleMouseDown = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const handle = target.closest('[data-drag-handle="true"]');
        if (handle && cardRef.current) {
            cardRef.current.dataset.dragAllowed = 'true';
        }
    };

    const handleDragStart = (e: React.DragEvent) => {
        const card = cardRef.current;
        if (!card || card.dataset.dragAllowed !== 'true') {
            e.preventDefault();
            if (card) delete card.dataset.dragAllowed;
            return;
        }
        delete card.dataset.dragAllowed;
        onDragStart(e);
    };

    return (
        <div
            ref={cardRef}
            className="d-flex align-items-center gap-1 p-1 border rounded position-relative"
            style={{
                backgroundColor: lifePartColor + "18",
                borderColor: lifePartColor + "40",
                fontSize: "0.75rem",
                opacity: task.isDone ? 0.5 : 1,
            }}
            data-month-task-uuid={task.uuid}
            draggable={draggable}
            onMouseDown={handleMouseDown}
            onDragStart={handleDragStart}
        >
            {draggable && (
                <span
                    className="text-muted"
                    style={{ cursor: "grab", fontSize: "0.65rem" }}
                    data-drag-handle="true"
                >
                    <Icon icon={faGripVertical} style={{ fontSize: "0.6rem" }} />
                </span>
            )}
            <span
                className="text-truncate flex-grow-1"
                style={{
                    textDecoration: task.isDone ? "line-through" : "none",
                    color: task.isDone ? "#999" : "inherit",
                    cursor: onOpenTask ? "pointer" : "default",
                }}
                onClick={() => onOpenTask?.(task.uuid)}
                title={task.description}
            >
                {task.description}
            </span>
            <Button
                variant="transparent"
                size="sm"
                className="p-0 text-muted"
                style={{ fontSize: "0.65rem", lineHeight: 1 }}
                onClick={onRemove}
                disabled={readonly}
                title="Удалить из месяца"
            >
                <Icon icon={faTrash} style={{ fontSize: "0.6rem" }} />
            </Button>
        </div>
    );
}

/* ─── Сетка месяцев ─── */
export default function MonthGrid({
    lifepartUuid,
    tasks,
    roles,
    sortedRoles,
    onTaskMonthUpdate,
    onOpenTask,
    selectedMonth: externalSelectedMonth,
    onSelectedMonthChange,
    currentLifePartUuid,
    currentStepPoint,
}: Props) {
    const { readonly } = useReadonly();
    const [currentYear, setCurrentYear] = useState(() =>
        parseInt(format(new Date(), "yyyy"))
    );
    const [draggedTaskUuid, setDraggedTaskUuid] = useState<string | null>(null);
    const [hoveredMonth, setHoveredMonth] = useState<string | null>(null);
    const [dropdownTask, setDropdownTask] = useState<{ month: string; tasks: Task[] } | null>(null);
    const [internalSelectedMonth, setInternalSelectedMonth] = useState(() => new Date().getMonth() + 1);
    
    // Используем внешний selectedMonth если передан, иначе внутренний
    const selectedMonth = externalSelectedMonth !== undefined ? 
        (externalSelectedMonth ? parseInt(externalSelectedMonth.split('-')[1]) : internalSelectedMonth) 
        : internalSelectedMonth;
    
    // Уведомляем о смене месяца
    React.useEffect(() => {
        if (onSelectedMonthChange) {
            const monthKey = `${currentYear}-${String(selectedMonth).padStart(2, "0")}`;
            onSelectedMonthChange(monthKey);
        }
    }, [currentYear, selectedMonth, onSelectedMonthChange]);
    
    const today = new Date();
    const currentMonthKey = format(today, "yyyy-MM");

    const goToCurrentMonth = () => {
        setCurrentYear(parseInt(format(today, "yyyy")));
        if (externalSelectedMonth !== undefined) {
            onSelectedMonthChange?.(`${currentYear}-${String(today.getMonth() + 1).padStart(2, "0")}`);
        } else {
            setInternalSelectedMonth(today.getMonth() + 1);
        }
    };

    const prevMonth = () => {
        if (selectedMonth === 1) {
            if (externalSelectedMonth !== undefined) {
                onSelectedMonthChange?.(`${currentYear - 1}-12`);
            } else {
                setInternalSelectedMonth(12);
                setCurrentYear((y) => y - 1);
            }
        } else {
            if (externalSelectedMonth !== undefined) {
                onSelectedMonthChange?.(`${currentYear}-${String(selectedMonth - 1).padStart(2, "0")}`);
            } else {
                setInternalSelectedMonth((m) => m - 1);
            }
        }
    };

    const nextMonth = () => {
        if (selectedMonth === 12) {
            if (externalSelectedMonth !== undefined) {
                onSelectedMonthChange?.(`${currentYear + 1}-01`);
            } else {
                setInternalSelectedMonth(1);
                setCurrentYear((y) => y + 1);
            }
        } else {
            if (externalSelectedMonth !== undefined) {
                onSelectedMonthChange?.(`${currentYear}-${String(selectedMonth + 1).padStart(2, "0")}`);
            } else {
                setInternalSelectedMonth((m) => m + 1);
            }
        }
    };

    const handleMonthSelect = (month: number) => {
        if (externalSelectedMonth !== undefined) {
            onSelectedMonthChange?.(`${currentYear}-${String(month).padStart(2, "0")}`);
        } else {
            setInternalSelectedMonth(month);
        }
    };

    // Задачи, привязанные к этому году (у которых month содержит хотя бы один месяц этого года)
    const yearTasks = useMemo(() => {
        const prefix = `${currentYear}-`;
        return Object.values(tasks).filter(
            (t) =>
                t.month &&
                t.month.some((m) => m.startsWith(prefix))
        );
    }, [tasks, currentYear]);

    // Группировка по месяцу
    const monthGroups = useMemo(() => {
        const groups: Record<string, Task[]> = {};
        for (let m = 1; m <= 12; m++) {
            const key = `${currentYear}-${String(m).padStart(2, "0")}`;
            groups[key] = yearTasks.filter(
                (t) => t.month && t.month.includes(key)
            );
        }

        // Сортируем задачи внутри каждого месяца по порядку ролей
        const roleOrder = new Map(sortedRoles.map((r, i) => [r.uuid, i]));
        Object.keys(groups).forEach((key) => {
            groups[key].sort((a, b) => {
                const orderA = roleOrder.get(a.lifepart) ?? 999;
                const orderB = roleOrder.get(b.lifepart) ?? 999;
                return orderA - orderB;
            });
        });

        return groups;
    }, [yearTasks, currentYear, sortedRoles]);

    /* ── Drag & Drop ── */
    const handleDragStart = useCallback(
        (e: React.DragEvent, taskUuid: string) => {
            setDraggedTaskUuid(taskUuid);
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", taskUuid);
        },
        []
    );

    const handleDropOnMonth = useCallback(
        (e: React.DragEvent, monthKey: string) => {
            if (readonly) return;
            e.preventDefault();
            setHoveredMonth(null);
            const taskUuid = e.dataTransfer.getData("text/plain") || draggedTaskUuid;
            if (!taskUuid) return;

            const task = tasks[taskUuid];
            if (!task) {
                setDraggedTaskUuid(null);
                return;
            }

            const currentMonths = task.month || [];
            const updatedMonths = currentMonths.includes(monthKey)
                ? currentMonths
                : [...currentMonths, monthKey];

            onTaskMonthUpdate(taskUuid, updatedMonths);
            setDraggedTaskUuid(null);
        },
        [tasks, draggedTaskUuid, onTaskMonthUpdate]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    }, []);

    const handleRemoveFromMonth = useCallback(
        (taskUuid: string, monthKey: string) => {
            if (readonly) return;
            const task = tasks[taskUuid];
            if (!task) return;
            const currentMonths = task.month || [];
            const updatedMonths = currentMonths.filter((m) => m !== monthKey);
            onTaskMonthUpdate(taskUuid, updatedMonths);
        },
        [tasks, readonly, onTaskMonthUpdate]
    );

    const getLifePartColor = (task: Task): string => {
        const lp = roles.objects[task.lifepart];
        return lp?.color || "#6c757d";
    };

    /* ── Навигация по годам ── */
    const prevYear = () => setCurrentYear((y) => y - 1);
    const nextYear = () => setCurrentYear((y) => y + 1);

    return (
        <Card className="shadow-sm" style={{

        }}>
            <Card.Header
                className="d-flex flex-row justify-content-between align-items-center"
                style={{ backgroundColor: "rgba(13, 110, 253, 0.08)" }}
            >
                <strong style={{ color: "var(--bs-primary)" }}>
                    <Icon icon={faCalendarDay} className="me-2" />
                    Планирование по месяцам
                </strong>
                <div className="d-flex align-items-center gap-2">
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Предыдущий год</Tooltip>}
                    >
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={prevYear}
                            style={{ width: 32, height: 32, minWidth: 32, padding: 0 }}
                        >
                            <Icon icon={faChevronLeft} size="sm" />
                        </Button>
                    </OverlayTrigger>
                    <span
                        className="fw-bold"
                        style={{ minWidth: 60, textAlign: "center" }}
                    >
                        {currentYear}
                    </span>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Следующий год</Tooltip>}
                    >
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={nextYear}
                            style={{ width: 32, height: 32, minWidth: 32, padding: 0 }}
                        >
                            <Icon icon={faChevronRight} size="sm" />
                        </Button>
                    </OverlayTrigger>
                </div>
            </Card.Header>

            {/* Desktop — сетка 12 месяцев */}
            <Card.Body className="p-0 d-none d-lg-block">
                <div className="row g-0 p-3">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
                        const monthKey = `${currentYear}-${String(month).padStart(2, "0")}`;
                        const monthTasks = monthGroups[monthKey] || [];
                        const isHovered = hoveredMonth === monthKey;
                        const isCurrentMonth = currentYear === parseInt(format(today, "yyyy")) && month === (today.getMonth() + 1);

                        return (
                            <div
                                key={monthKey}
                                className="col-6 col-md-4 col-lg-4 col-xl-4 mb-3"
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDropOnMonth(e, monthKey)}
                                onDragEnter={() => setHoveredMonth(monthKey)}
                                onDragLeave={() => setHoveredMonth(null)}
                            >
                                <div
                                    className="rounded border h-100 p-2 d-flex flex-column"
                                    style={{
                                        backgroundColor: isCurrentMonth
                                            ? "rgba(13, 110, 253, 0.1)"
                                            : isHovered
                                                ? "rgba(13, 110, 253, 0.06)"
                                                : "transparent",
                                        borderColor: isCurrentMonth
                                            ? "var(--bs-primary)"
                                            : isHovered
                                                ? "var(--bs-primary)"
                                                : "var(--bs-border-color)",
                                        transition: "all 0.15s ease",
                                        minHeight: 100,
                                        boxShadow: isCurrentMonth ? "0 0 0 1px var(--bs-primary)" : "none",
                                    }}
                                >
                                    {/* Заголовок месяца */}
                                    <div
                                        className="fw-bold text-center mb-2 d-flex align-items-center justify-content-between"
                                        style={{ fontSize: "0.8rem" }}
                                    >
                                        <div>
                                            {MONTH_NAMES_SHORT[month - 1]}
                                        </div>
                                        <Dropdown align="end">
                                            <Dropdown.Toggle
                                                variant="transparent"
                                                size="sm"
                                                id={`dropdown-month-${monthKey}`}
                                                disabled={readonly}
                                                style={{
                                                    fontSize: "0.65rem",
                                                    padding: "0 2px",
                                                    color: "var(--bs-secondary)",
                                                }}
                                            >
                                                <Icon icon={faPlus} style={{ fontSize: "0.6rem" }} />
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Header style={{ fontSize: "0.7rem" }}>
                                                    Добавить задачи на {MONTH_NAMES[month - 1]}
                                                </Dropdown.Header>
                                                {(() => {
                                                    const available = Object.values(tasks).filter(
                                                        (t) =>
                                                            t.forPoint != null &&
                                                            t.lifepart === lifepartUuid &&
                                                            !t.month?.includes(monthKey) &&
                                                            !t.isDone
                                                    );
                                                    if (available.length === 0) {
                                                        return (
                                                            <Dropdown.Item
                                                                disabled
                                                                style={{ fontSize: "0.7rem" }}
                                                            >
                                                                Нет доступных задач
                                                            </Dropdown.Item>
                                                        );
                                                    }
                                                    return available.slice(0, 10).map((t) => (
                                                        <Dropdown.Item
                                                            key={t.uuid}
                                                            disabled={readonly}
                                                            onClick={() => {
                                                                const currentMonths = t.month || [];
                                                                onTaskMonthUpdate(t.uuid, [
                                                                    ...currentMonths,
                                                                    monthKey,
                                                                ]);
                                                                setDropdownTask(null);
                                                            }}
                                                            style={{ fontSize: "0.75rem" }}
                                                        >
                                                            <span
                                                                style={{
                                                                    display: "inline-block",
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: "50%",
                                                                    backgroundColor: getLifePartColor(t),
                                                                    marginRight: 6,
                                                                }}
                                                            />
                                                            {t.description}
                                                        </Dropdown.Item>
                                                    ));
                                                })()}
                                                {(() => {
                                                    const available = Object.values(tasks).filter(
                                                        (t) =>
                                                            t.forPoint != null &&
                                                            t.lifepart === lifepartUuid &&
                                                            !t.month?.includes(monthKey) &&
                                                            !t.isDone
                                                    );
                                                    if (available.length > 10) {
                                                        return (
                                                            <Dropdown.Divider />
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>

                                    {/* Карточки задач */}
                                    <div className="flex-grow-1 d-flex flex-column gap-1">
                                        {monthTasks.map((task) => (
                                            <MonthTaskCard
                                                key={task.uuid}
                                                task={task}
                                                lifePartColor={getLifePartColor(task)}
                                                onRemove={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveFromMonth(task.uuid, monthKey);
                                                }}
                                                onDragStart={(e) => handleDragStart(e, task.uuid)}
                                                draggable={!readonly}
                                                onOpenTask={onOpenTask}
                                                readonly={readonly}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card.Body>

            {/* Mobile — один месяц с навигацией */}
            <Card.Body className="d-lg-none p-3">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={prevMonth}
                    >
                        <Icon icon={faChevronLeft} />
                    </Button>
                    <div className="text-center">
                        <div className="fw-bold" style={{ fontSize: "1.1rem" }}>
                            {MONTH_NAMES[selectedMonth - 1]}
                        </div>
                        <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                            {currentYear}
                        </div>
                    </div>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={nextMonth}
                    >
                        <Icon icon={faChevronRight} />
                    </Button>
                </div>

                {/* Текущий месяц индикатор */}
                {selectedMonth === (today.getMonth() + 1) && currentYear === parseInt(format(today, "yyyy")) && (
                    <div className="badge bg-primary mb-3">
                        <Icon icon={faCalendarDay} className="me-1" />
                        Текущий месяц
                    </div>
                )}

                {/* Мини-календарь навигация */}
                <div className="d-flex flex-wrap gap-1 mb-3 justify-content-center">
                    {MONTH_NAMES_SHORT.map((name, idx) => {
                        const m = idx + 1;
                        const mKey = `${currentYear}-${String(m).padStart(2, "0")}`;
                        const isSelected = m === selectedMonth;
                        const isCurrent = m === (today.getMonth() + 1) && currentYear === parseInt(format(today, "yyyy"));
                        return (
                            <Button
                                key={m}
                                variant={isSelected ? "primary" : isCurrent ? "outline-primary" : "outline-secondary"}
                                size="sm"
                                style={{
                                    fontSize: "0.7rem",
                                    padding: "2px 6px",
                                    minWidth: 36,
                                }}
                                onClick={() => handleMonthSelect(m)}
                            >
                                {name}
                            </Button>
                        );
                    })}
                </div>

                {/* Задачи выбранного месяца */}
                <div
                    className="rounded border p-3"
                    style={{
                        backgroundColor: "rgba(13, 110, 253, 0.04)",
                        borderColor: "var(--bs-primary)",
                    }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDropOnMonth(e, `${currentYear}-${String(selectedMonth).padStart(2, "0")}`)}
                >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="fw-bold">
                            {MONTH_NAMES[selectedMonth - 1]}
                        </div>
                        <Dropdown align="end">
                            <Dropdown.Toggle
                                variant="outline-primary"
                                size="sm"
                                id={`dropdown-mobile-${selectedMonth}`}
                                disabled={readonly}
                                style={{ fontSize: "0.75rem" }}
                            >
                                <Icon icon={faPlus} className="me-1" />
                                Добавить
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {(() => {
                                    const monthKey = `${currentYear}-${String(selectedMonth).padStart(2, "0")}`;
                                    const allTasks = Object.values(tasks);
                                    
                                    const tasksInStep = allTasks.filter(t => 
                                        t.lifepart === lifepartUuid && 
                                        t.forPoint === currentStepPoint && 
                                        !t.parentTask
                                    );
                                    
                                    const available = allTasks.filter(
                                        (t) => {
                                            const matchesLifePart = t.lifepart === lifepartUuid;
                                            const matchesCurrentLifePart = !currentLifePartUuid || t.lifepart === currentLifePartUuid;
                                            const matchesStep = currentStepPoint == null || t.forPoint === currentStepPoint;
                                            const notInMonth = !t.month?.includes(monthKey);
                                            const notDone = !t.isDone;
                                            const hasForPoint = t.forPoint != null;
                                            
                                            return hasForPoint && matchesLifePart && matchesCurrentLifePart && matchesStep && notInMonth && notDone;
                                        }
                                    );
                                    
                                    if (available.length === 0) {
                                        return (
                                            <Dropdown.Item disabled style={{ fontSize: "0.75rem" }}>
                                                Нет доступных результатов
                                            </Dropdown.Item>
                                        );
                                    }
                                    return available.map((t) => (
                                        <Dropdown.Item
                                            key={t.uuid}
                                            disabled={readonly}
                                            onClick={() => {
                                                const currentMonths = t.month || [];
                                                onTaskMonthUpdate(t.uuid, [
                                                    ...currentMonths,
                                                    monthKey,
                                                ]);
                                            }}
                                            style={{ fontSize: "0.8rem" }}
                                        >
                                            <span
                                                style={{
                                                    display: "inline-block",
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: "50%",
                                                    backgroundColor: getLifePartColor(t),
                                                    marginRight: 6,
                                                }}
                                            />
                                            {t.description}
                                        </Dropdown.Item>
                                    ));
                                })()}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    <div className="d-flex flex-column gap-2">
                        {monthGroups[`${currentYear}-${String(selectedMonth).padStart(2, "0")}`]?.map((task) => (
                            <MonthTaskCard
                                key={task.uuid}
                                task={task}
                                lifePartColor={getLifePartColor(task)}
                                onRemove={(e) => {
                                    e.stopPropagation();
                                    handleRemoveFromMonth(task.uuid, `${currentYear}-${String(selectedMonth).padStart(2, "0")}`);
                                }}
                                onDragStart={(e) => handleDragStart(e, task.uuid)}
                                draggable={!readonly}
                                onOpenTask={onOpenTask}
                                readonly={readonly}
                            />
                        ))}
                    </div>

                    {(!monthGroups[`${currentYear}-${String(selectedMonth).padStart(2, "0")}`] ||
                        monthGroups[`${currentYear}-${String(selectedMonth).padStart(2, "0")}`].length === 0) && (
                            <div className="text-center text-muted py-4" style={{ fontSize: "0.85rem" }}>
                                Не выбраны желаемые результаты на этот месяц
                            </div>
                        )}
                </div>
            </Card.Body>
        </Card>
    );
}
